import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@shoflo/polymer/components/Button/Button';
import { Input } from '@shoflo/polymer/components/Input/Input';
import { InputLabel } from '@shoflo/polymer/components/InputLabel/InputLabel';
import { UserValidator } from '@shoflo/polymer/validators/UserValidator/UserValidator';
import { PasswordTooltip } from '@shoflo/polymer/components/PasswordTooltip/PasswordTooltip';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { Form, FormGroup } from 'reactstrap';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { signUpInvited } from 'actions/user';

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const PendingUserForm = ({ invitedUser }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { email } = invitedUser;
  const { control, register, handleSubmit } = useForm({
    defaultValues: {
      email
    },
    mode: 'all'
  });
  const password = useWatch({
    control,
    name: 'password', // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both
    defaultValue: '' // default value before the render
  });

  const submitText = !isSubmitting
    ? 'Complete sign up'
    : 'Good things are happening...';
  const { errors, isValid } = useFormState({ control });

  const submitDisabled = !isValid;

  return (
    <Form
      onSubmit={handleSubmit(async formValues => {
        setIsSubmitting(true);
        dispatch(signUpInvited(formValues));
      })}
    >
      <FormGroup>
        <InputLabel for="fullName">Full name</InputLabel>
        <Input
          id="fullName"
          data-testid="fullName-input"
          placeholder="e.g. Tim Bergling"
          error={errors.fullName}
          {...register('fullName', {
            required: true,
            validate: val =>
              UserValidator.fullName(val) ||
              'Uh oh! A first and last name is required'
          })}
          autoComplete="off"
        />
      </FormGroup>
      <FormGroup>
        <InputLabel for="email">Email</InputLabel>
        <Input
          disabled
          id="email"
          name="email"
          data-testid="email-input"
          placeholder="e.g. tim.berg@gmail.com"
          error={errors.email}
          {...register('email', {
            validate: {}
          })}
          autoComplete="username"
        />
      </FormGroup>
      <FormGroup>
        <InputLabel for="password">Password</InputLabel>
        <Input
          id="password"
          name="password"
          data-testid="password-input"
          type="password"
          placeholder="Enter a password"
          error={errors.password}
          {...register('password', {
            required: true,
            validate: val =>
              UserValidator.password(val) || 'Uh oh! Password is invalid'
          })}
          autoComplete="new-password"
        />
        {errors.password && (
          <PasswordTooltip
            targetElement="password"
            password={password}
            placement="top-end"
          />
        )}
      </FormGroup>
      <StyledButtonContainer>
        <Button type="submit" disabled={isSubmitting || submitDisabled}>
          {submitText}
        </Button>
      </StyledButtonContainer>
    </Form>
  );
};

PendingUserForm.propTypes = {
  invitedUser: PropTypes.object.isRequired
};

export default PendingUserForm;
